import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import WireBox from "./WireBox";

const HERO_IMG =
  "https://images.unsplash.com/photo-1611505908502-5b67e53e3a76?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwzfHwzZCUyMHByaW50ZXIlMjBwcmludGluZ3xlbnwwfHx8fDE3OTAyNDY5NTd8MA&ixlib=rb-4.1.0&q=85";

const line = {
  hidden: { y: "112%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 0.95, delay: 0.15 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section ref={ref} className="dot-grid relative overflow-hidden">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 pb-20 pt-14 lg:grid-cols-12 lg:px-12 lg:pb-28 lg:pt-24">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="inline-flex items-center gap-3 rounded-full border border-black/10 bg-white px-4 py-1.5"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-accent" />
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-600">
              Atelier de fabrication digitale — Octobre 2026
            </span>
          </motion.div>

          <h1 className="mt-7 font-display text-4xl font-black leading-[1.06] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            <span className="block overflow-hidden pb-1">
              <motion.span className="block" variants={line} initial="hidden" animate="show" custom={0}>
                Donnez vie à vos idées.
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span className="block" variants={line} initial="hidden" animate="show" custom={1}>
                Réparez <span className="text-accent">l'irremplaçable.</span>
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 max-w-xl text-base leading-relaxed text-neutral-600 sm:text-lg"
          >
            Impression 3D et modélisation 3D sur demande : pièces uniques, réparation de pièces
            cassées, organizer et rangements sur mesure. Devis gratuit sous 48 h ouvrées.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.62, ease: [0.16, 1, 0.3, 1] }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/devis"
              data-testid="hero-estimate-button"
              className="group inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:scale-[1.03] hover:bg-accent"
            >
              Demander mon devis en 2 min
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-services-button"
              className="inline-flex items-center rounded-full border border-black/15 bg-white px-7 py-3.5 text-sm font-semibold text-ink transition-colors hover:border-ink"
            >
              Découvrir les services
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500"
          >
            {["Fichiers .STL & .STEP fournis", "Impression haute précision", "Réponse sous 48 h ouvrées"].map((t) => (
              <span key={t} className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-accent" /> {t}
              </span>
            ))}
          </motion.div>
        </div>

        <motion.div style={{ y: cardY }} className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="corner-card relative overflow-hidden rounded-[20px] border border-black/10 bg-white shadow-[0_24px_60px_-30px_rgba(0,0,0,0.25)]"
            data-testid="hero-visual-card"
          >
            <img
              src={HERO_IMG}
              alt="Buse d'imprimante 3D en pleine impression dans l'atelier"
              className="h-72 w-full object-cover sm:h-80"
            />
            <div className="absolute left-4 top-4 rounded-full bg-white/90 px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-ink backdrop-blur">
              Buse 0.4 mm — fabrication additive
            </div>
            <div className="relative -mt-10 border-t border-black/5 bg-white p-5">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                  Spécimen — vérification dimensionnelle
                </span>
                <span className="font-mono text-[10px] text-accent">ISO VUE</span>
              </div>
              <WireBox L={120} W={80} H={60} className="mt-1 h-44 w-full" />
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
